import React, { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  CardActions,
  Divider,
  Button,
  Box,
  Grid,
  Typography,
  TextField,
  Tooltip,
  Snackbar
} from '@material-ui/core';
import PrintTemplateEditor from 'components/Template/PrintTemplateEditor';
import { constants } from 'views/Campaign';
import SaveMessage from 'views/UserMessaging/customModalContent/saveMessage';
import SaveConfirm from 'views/UserMessaging/customModalContent/saveConfirmTemplate';
import { useSelector } from 'react-redux';
import { Alert } from '@material-ui/lab';
import { CloseIcon } from 'components';
import { isReadOnlyMode } from 'store/helpers/helperFunctions';
import { useLanguageSettings } from '../../../Settings/useLanguageSettings';

const useStyles = makeStyles(theme => ({
  root: {},
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  },
  autoSave: {
    marginLeft: '40%'
  },
  cardHeader: {},
  cardActions: {
    flexDirection: 'row'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20%'
  }
}));

const Design = props => {
  const { className, staticContext, template, back, showNav, isIFrame, ...rest } = props;
  const {
    allSurveys,
    isLoading,
    isTestTemplateLoading,
    shouldAutoSave,
    isAutoSaveSuccess,
    isTemplateSaved,
    isTemplateSaving,
    isTestDownloadError,
    goBack,
    navBackOnError,
    thisTemplate,
    handleChange,
    saveTemplate,
    onClickTestTemplate,
    onFocus,
    onBlur,
    closeTemplate,
    shouldShowError,
    getErrorMessage,
    updateCurrentErrorValidationSegment,
    validateTestTemplate,
    updateCurrentTemplate,
    startAutoSaveTimer,
    autoSaveTemplate,
    isTenantDefaultTemplate,
    showWarningMessage,
    isTemplateReadOnly,
    onClickTestTemplateClose
  } = template;

  const {
    campaignOccurrenceTypes: { INTERNAL_SURVEY_RESPONSE, EXTERNAL_SURVEY_RESPONSE }
  } = constants;

  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [isValidationError, setIsValidationError] = useState({});
  const { templateLanguageContent, getTemplateLanguageContent } = useLanguageSettings();
  const [detailsContent, setDetailsContent] = useState(null);

  const classes = useStyles();
  const editorRef = useRef(null);

  useEffect(() => {
    // startAutoSaveTimer();
  }, []);

  useEffect(() => {
    updateCurrentErrorValidationSegment('design');
    if(!isIFrame) {
      navBackOnError(back);
    }
  }, [thisTemplate, updateCurrentErrorValidationSegment]);

  useEffect(() => {
    if (isTemplateSaved) {
      startAutoSaveTimer();
    }
  }, [isTemplateSaved]);

  useEffect(() => {
    if (shouldAutoSave) {
      editorRef.current?.exportAndSaveTemplate({ type: 'autoSaveTemplate' });
    }
  }, [shouldAutoSave]);

  useEffect(() => {
    if (templateLanguageContent) {
      setDetailsContent(getTemplateLanguageContent("printTemplateDesign"));
    }
  }, [templateLanguageContent]);

  const onClickSaveButton = () => {
    if (showWarningMessage()) {
      setShowSaveConfirmModal(true);
    } else {
      editorRef.current?.exportAndSaveTemplate();
    }
  };

  const saveConfirmTemplate = () => {
    setShowSaveConfirmModal(false);
    editorRef.current?.exportAndSaveTemplate();
  };

  const closeSaveConfirm = () => {
    setShowSaveConfirmModal(false);
  };

  const validateErrors = () => {
    let err = Object.values(isValidationError).filter(val => val.isDisabled);
    return err.length > 0 ? true : false;
  };

  return (
    <div className={classes.root}>
      {thisTemplate?.eventMetaData ? (
        <Card {...rest} className={clsx(classes.root, className)}>
          <form autoComplete="off">
            <React.Fragment>
              <div>
                {isReadOnlyMode() ? (
                    <Alert severity="info">
                      {detailsContent?.readOnlyModeAlert || `You can only view this and do not have access to edit.`}
                    </Alert>
                  ): (
                    isTemplateReadOnly() && (
                      <Alert severity="info">
                        {detailsContent?.templateReadOnlyModeAlert || `You can only view this template and do not have access to edit.`}{' '}
                        {isTenantDefaultTemplate()
                          ? `${detailsContent?.tenantDefaultAlert || `Tenant Default template can be edited only with 'ALL' property.`}`
                          : `${detailsContent?.globalDefaultAlert || `Only admin can edit Global Default template.`}`}
                      </Alert>)
                )}
              </div>
              <div className={classes.headerContainer}>
                <div className={classes.title}>
                  <CardHeader
                    subheader={detailsContent?.pageSubHeader || `* indicates a required field`}
                    title={detailsContent?.pageTitle || `Template Design`}
                    titleTypographyProps={{ variant: 'h1' }}
                  />
                </div>
                {!isIFrame && (
                  <CloseIcon onClickClose={closeTemplate} />
                )}
              </div>
            </React.Fragment>
            <CardContent>
              {thisTemplate?.eventMetaData && (
                <PrintTemplateEditor
                  ref={editorRef}
                  thisTemplate={thisTemplate}
                  templateKeys={thisTemplate.eventMetaData}
                  surveys={(
                    thisTemplate.campaignOccurrenceType !==
                    INTERNAL_SURVEY_RESPONSE &&
                    thisTemplate.campaignOccurrenceType !==
                    EXTERNAL_SURVEY_RESPONSE)
                      ? allSurveys
                      : []
                  }
                  useUnsubscribeLinkOption={false}
                  isLoading={isLoading}
                  handleChange={handleChange}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  saveTemplate={saveTemplate}
                  shouldAutoSave={shouldAutoSave}
                  autoSaveTemplate={autoSaveTemplate}
                  setIsValidationError={setIsValidationError}>
                  {shouldAutoSave && (
                    <Typography
                      color="textSecondary"
                      variant="body2"
                      className={classes.autoSave}>
                      {detailsContent?.autoSavingLabel || `AutoSaving...`}
                    </Typography>
                  )}
                </PrintTemplateEditor>
              )}
            </CardContent>
            {(back || showNav) && (
              <>
                <Divider />
                <CardActions className={classes.cardActions}>
                  {!isIFrame && (
                    <Button
                      color="secondary"
                      variant="contained"
                      disabled={isLoading}
                      link={back}
                      onClick={goBack}>
                      {detailsContent?.backButtonLabel || `Back`}
                    </Button>
                  )}
                  <Typography></Typography>
                  <Box flexGrow={1}>&nbsp;</Box>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={isLoading || isTestTemplateLoading || isTemplateSaving}
                    onClick={() => onClickTestTemplate(editorRef.current.getEditorData())}>
                    {detailsContent?.testTemplateButtonLabel|| `Test Template`}
                  </Button>
                  {!isTemplateReadOnly() &&
                    (validateErrors() ? (
                      <Tooltip title={detailsContent?.saveTemplateErroHelper || `Fix the handlebar formats`}>
                        <span>
                          <Button
                            color="primary"
                            variant="contained"
                            disabled={() => validateErrors}
                            onClick={() => onClickSaveButton()}>
                            {detailsContent?.saveTemplateButtonLabel || `Save Template`}
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={isLoading || isTemplateSaving}
                        onClick={() => onClickSaveButton()}>
                        {detailsContent?.saveTemplateButtonLabel || `Save Template`}
                      </Button>
                    ))}
                </CardActions>
              </>
            )}
          </form>
        </Card>
      ) : (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      )}
      {!isAutoSaveSuccess && <SaveMessage openSnackbar={isTemplateSaved} />}
      {<Snackbar
        open={isTestDownloadError}
        autoHideDuration={6000}
        onClose={onClickTestTemplateClose}>
        <Alert onClose={onClickTestTemplateClose} severity="warning">
          {detailsContent?.saveTemplateErrorFailedMessage || `Test Template Failed`}
        </Alert>
      </Snackbar>}
      {showSaveConfirmModal && (
        <SaveConfirm
          closeModel={closeSaveConfirm}
          saveConfirmTemplate={saveConfirmTemplate}></SaveConfirm>
      )}
    </div>
  );
};

export default Design;
